import { detectCreditCard } from '@vend/credit-card-detector'
import { ApiCustomer } from '@vend/types'
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  FulfilmentDetail,
  FulfilmentType,
  isDefinedFulfilmentType,
  isNoteTooLong,
} from '../../../../../models/fulfilment'
import { useAppSelector } from '../../../../../state/hooks'
import {
  selectLineItem,
  selectSaleCustomer,
} from '../../../../../state/slices/currentSale/currentSaleSlice'
import {
  selectIsSaleCustomerContactInfoReviewed,
  selectOpenFulfilmentPopoverLineItemId,
} from '../../../../../state/slices/uiStateSlice'
import { PartialCustomerGroup } from '../../../../react/modals/common/types'
import { WALKIN_CUSTOMER_CODE } from '../../../sale-model.constant'
import { isMissingContactInfo } from './LineItemFulfillmentUtils'
import { useFulfillmentHelper } from './hooks/useFulfillmentHelper'

interface LineItemFulfillmentContextProps {
  currentView: CurrentView
  setCurrentView: React.Dispatch<React.SetStateAction<CurrentView>>
  currentDecision: ShouldFulfillOpt
  setCurrentDecision: React.Dispatch<React.SetStateAction<ShouldFulfillOpt>>
  currentCustomer: ApiCustomer | null
  setCurrentCustomer: React.Dispatch<React.SetStateAction<ApiCustomer | null>>
  originalCustomer: ApiCustomer | null
  setOriginalCustomer: React.Dispatch<React.SetStateAction<ApiCustomer | null>>
  setIsCurrentCustomerRemoved: React.Dispatch<React.SetStateAction<boolean>>
  customerGroup: PartialCustomerGroup | undefined
  setCustomerGroup: React.Dispatch<
    React.SetStateAction<PartialCustomerGroup | undefined>
  >
  isEmailInvalid: boolean
  setIsEmailInvalid: React.Dispatch<React.SetStateAction<boolean>>
  isLoading: boolean
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  fulfilmentDetails: FulfilmentDetail
  setFulfilmentDetails: React.Dispatch<React.SetStateAction<FulfilmentDetail>>
  selectedFulfilmentOutletId: string | undefined
  setSelectedFulfilmentOutletId: React.Dispatch<
    React.SetStateAction<string | undefined>
  >
  isFulfilmentNoteInvalid: boolean
  onSelectContinueAnyway?: () => void
  onSelectFulfilmentType?: (
    type: FulfilmentType,
    fulfilmentOutletId?: string
  ) => void
  isBreakdownSelected: boolean
  setIsBreakdownSelected: React.Dispatch<React.SetStateAction<boolean>>
}

export enum ShouldFulfillOpt {
  NO = 'no',
  YES = 'yes',
}

const LineItemFulfillmentContext = createContext<
  LineItemFulfillmentContextProps | undefined
>(undefined)

export enum CurrentView {
  DECISION = 'decision',
  ADD_CUSTOMER = 'add_customer',
  CREATE_NEW_CUSTOMER = 'create_new_customer',
  REVIEW_CONTACT_INFO = 'review_contact_info',
  FULFILMENT_METHOD = 'fulfilment_method',
  FIX_SALE_ADDRESSES = 'fix_sale_addresses',
}

export const LineItemFulfillmentProvider: React.FC<{
  children: ReactNode
  defaultView?: CurrentView
  defaultFulfillmentDetail?: FulfilmentDetail
  onSelectContinueAnyway?: () => void
  onSelectFulfilmentType?: (
    type: FulfilmentType,
    fulfilmentOutletId?: string
  ) => void
}> = ({
  children,
  defaultView,
  defaultFulfillmentDetail = {
    type: FulfilmentType.UNSPECIFIED,
    note: '',
  },
  onSelectContinueAnyway,
  onSelectFulfilmentType,
}) => {
  const saleCustomer = useAppSelector(selectSaleCustomer)
  const isContactInfoReviewed = useAppSelector(
    selectIsSaleCustomerContactInfoReviewed
  )
  const lineItemId = useAppSelector(selectOpenFulfilmentPopoverLineItemId)
  const lineItem = useAppSelector(selectLineItem(lineItemId))

  const [currentDecision, setCurrentDecision] = useState<ShouldFulfillOpt>(
    ShouldFulfillOpt.NO
  )
  const [isEmailInvalid, setIsEmailInvalid] = useState(false)
  const [customerGroup, setCustomerGroup] = useState<
    PartialCustomerGroup | undefined
  >(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [fulfilmentDetails, setFulfilmentDetails] = useState<FulfilmentDetail>(
    defaultFulfillmentDetail
  )
  const [selectedFulfilmentOutletId, setSelectedFulfilmentOutletId] = useState<
    string | undefined
  >(lineItem?.fulfilmentOutletId)

  const { shouldFixFulfillmentAddresses } = useFulfillmentHelper()
  const needToFixSaleAddresses = shouldFixFulfillmentAddresses(
    fulfilmentDetails
  )

  // Product Packaging Breakdown
  const [isBreakdownSelected, setIsBreakdownSelected] = useState(false)

  // Setup customer
  let initialCurrentCustomer = null
  let initialOriginalCustomer = null
  const isWalkinCustomer = saleCustomer?.customer_code === WALKIN_CUSTOMER_CODE

  if (!isWalkinCustomer) {
    initialCurrentCustomer = saleCustomer
    initialOriginalCustomer = saleCustomer
  }

  const [currentCustomer, setCurrentCustomer] = useState<ApiCustomer | null>(
    initialCurrentCustomer
  )
  const [originalCustomer, setOriginalCustomer] = useState<ApiCustomer | null>(
    initialOriginalCustomer
  )
  const [
    isCurrentCustomerRemoved,
    setIsCurrentCustomerRemoved,
  ] = useState<boolean>(false)

  // Setup view
  const getCustomerDrivenView = (
    customer: ApiCustomer | null,
    fulfilmentDetail: FulfilmentDetail
  ) => {
    if (!customer) {
      return CurrentView.ADD_CUSTOMER
    }
    if (isMissingContactInfo(customer) || !isContactInfoReviewed) {
      return CurrentView.REVIEW_CONTACT_INFO
    }
    if (
      lineItem?.fulfilmentType === FulfilmentType.DELIVERY &&
      shouldFixFulfillmentAddresses(fulfilmentDetail)
    ) {
      return CurrentView.FIX_SALE_ADDRESSES
    }
    return CurrentView.FULFILMENT_METHOD
  }

  let initialView = defaultView || CurrentView.DECISION

  // The inventory decision is only applicable before a fulfillment method is
  // selected. The line item can still carry the inventory warning override
  // after an address modal closes and remounts this provider.
  if (isDefinedFulfilmentType(lineItem?.fulfilmentType)) {
    initialView = getCustomerDrivenView(currentCustomer, fulfilmentDetails)
  }

  // Adjust for delivery address issues when defaultView is set
  if (
    defaultView &&
    lineItem?.fulfilmentType === FulfilmentType.DELIVERY &&
    fulfilmentDetails.type === FulfilmentType.DELIVERY &&
    needToFixSaleAddresses
  ) {
    initialView = CurrentView.FIX_SALE_ADDRESSES
  }

  const [currentView, setCurrentView] = useState<CurrentView>(initialView)

  // Reset view on line item change, but not mid customer sub-flow (avoids
  // ejecting the cashier or crashing CustomerBadge on a draft — LRX-42968).
  // Exception: clearing the fulfilment type back to unspecified drops the
  // sub-flow and its draft too, so picking a type again starts fresh instead
  // of resurfacing stale data via REVIEW_CONTACT_INFO.
  const prevLineItemIdRef = useRef(lineItemId)
  const prevFulfilmentTypeRef = useRef(lineItem?.fulfilmentType)
  useEffect(() => {
    setSelectedFulfilmentOutletId(lineItem?.fulfilmentOutletId)
    // Only resync details when the parent detail belongs to the current line
    // item type. During a method switch the parent can still contain the
    // previous type, and copying it here would undo the user's selection.
    if (defaultFulfillmentDetail.type === lineItem?.fulfilmentType) {
      setFulfilmentDetails(defaultFulfillmentDetail)
    }
    // Computed from the fresh prop, not the (about to be updated) state
    // above, so this reset doesn't apply a view based on stale data.
    const freshView = isDefinedFulfilmentType(lineItem?.fulfilmentType)
      ? getCustomerDrivenView(currentCustomer, defaultFulfillmentDetail)
      : initialView
    const fulfilmentTypeBecameUnspecified =
      lineItem?.fulfilmentType !== prevFulfilmentTypeRef.current &&
      !isDefinedFulfilmentType(lineItem?.fulfilmentType)
    const isActiveCustomerSubFlow =
      currentView === CurrentView.CREATE_NEW_CUSTOMER ||
      currentView === CurrentView.ADD_CUSTOMER
    const lineItemChanged = lineItemId !== prevLineItemIdRef.current
    const fulfilmentTypeChanged =
      lineItem?.fulfilmentType !== prevFulfilmentTypeRef.current

    if (lineItemId) {
      if (fulfilmentTypeBecameUnspecified) {
        setCurrentView(freshView)
        if (isActiveCustomerSubFlow) {
          setCurrentCustomer(initialCurrentCustomer)
          setOriginalCustomer(initialOriginalCustomer)
          setIsCurrentCustomerRemoved(false)
        }
      } else if (
        (lineItemChanged ||
          (fulfilmentTypeChanged && currentView === CurrentView.DECISION)) &&
        !isActiveCustomerSubFlow
      ) {
        setCurrentView(freshView)
      } else if (
        fulfilmentTypeChanged &&
        ((lineItem?.fulfilmentType === FulfilmentType.PICKUP &&
          currentView === CurrentView.FIX_SALE_ADDRESSES) ||
          freshView === CurrentView.FIX_SALE_ADDRESSES)
      ) {
        setCurrentView(freshView)
      }
    }
    prevLineItemIdRef.current = lineItemId
    prevFulfilmentTypeRef.current = lineItem?.fulfilmentType
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItemId, lineItem?.fulfilmentType])

  // currentCustomer is only seeded from saleCustomer once at mount, so if the
  // sale gets a customer afterwards (e.g. via the top-level customer search)
  // while this row never happened to be on ADD_CUSTOMER, it stayed null
  // forever. Resync whenever it's missing; only force the view when we were
  // actually showing the "add customer" search, matching prior behaviour.
  useEffect(() => {
    if (
      saleCustomer != null &&
      currentCustomer == null &&
      isCurrentCustomerRemoved === false &&
      saleCustomer?.customer_code !== WALKIN_CUSTOMER_CODE
    ) {
      setOriginalCustomer(saleCustomer)
      setCurrentCustomer(saleCustomer)
      if (currentView === CurrentView.ADD_CUSTOMER) {
        setCurrentView(CurrentView.REVIEW_CONTACT_INFO)
      }
    }
  }, [currentView, saleCustomer, currentCustomer, isCurrentCustomerRemoved])

  return (
    <LineItemFulfillmentContext.Provider
      value={{
        currentDecision,
        setCurrentDecision,
        currentView,
        setCurrentView,
        isEmailInvalid,
        setIsEmailInvalid,
        currentCustomer,
        setCurrentCustomer,
        originalCustomer,
        setOriginalCustomer,
        setIsCurrentCustomerRemoved,
        customerGroup,
        setCustomerGroup,
        isLoading,
        setIsLoading,
        fulfilmentDetails,
        setFulfilmentDetails,
        selectedFulfilmentOutletId,
        setSelectedFulfilmentOutletId,
        onSelectContinueAnyway,
        onSelectFulfilmentType,
        isFulfilmentNoteInvalid:
          detectCreditCard(fulfilmentDetails.note) ||
          isNoteTooLong(fulfilmentDetails.note),
        isBreakdownSelected,
        setIsBreakdownSelected,
      }}
    >
      {children}
    </LineItemFulfillmentContext.Provider>
  )
}

export const useLineItemFulfillmentContext = (): LineItemFulfillmentContextProps => {
  const context = useContext(LineItemFulfillmentContext)
  if (!context) {
    throw new Error(
      'useLineItemFulfillment must be used within a LineItemFulfillmentProvider'
    )
  }
  return context
}
